import React, { useState } from 'react';
import { X, Users, FileText, Save } from 'lucide-react';
import api from '../utils/api';

/**
 * CreateTeamModal - Simple modal for creating new teams
 */
const CreateTeamModal = ({ isOpen, onClose, onSuccess, initialData, initialParentId }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [existingTeams, setExistingTeams] = useState([]);
    const [employees, setEmployees] = useState([]);

    // Initialize form with defaults or initial data
    const [formData, setFormData] = useState({
        team_name: '',
        description: '',
        parent_id: '',
        leader_id: '',
        color: '#3b82f6',
    });

    React.useEffect(() => {
        if (isOpen) {
            if (initialData) {
                setFormData({
                    team_name: initialData.team_name || '',
                    description: initialData.description || '',
                    parent_id: initialData.parent_id || '',
                    leader_id: initialData.leader_id || '',
                    color: initialData.color || '#3b82f6',
                });
            } else {
                setFormData({
                    team_name: '',
                    description: '',
                    parent_id: initialParentId || '', // Pre-select parent if provided
                    leader_id: '',
                    color: '#3b82f6',
                });
            }
        }
    }, [isOpen, initialData, initialParentId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setError('');
    };

    // Fetch data when modal opens
    React.useEffect(() => {
        if (isOpen) {
            const fetchData = async () => {
                try {
                    const [teamsRes, employeesRes] = await Promise.all([
                        api.get('/teams'),
                        api.get('/employees')
                    ]);
                    setExistingTeams(teamsRes.data.teams || []);
                    setEmployees(employeesRes.data.employees || []);
                } catch (err) {
                    console.error('Failed to fetch modal data', err);
                }
            };
            fetchData();
        }
    }, [isOpen]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.team_name.trim()) {
            setError('Team name is required');
            return;
        }

        setLoading(true);
        setError('');

        try {
            let response;
            if (initialData && initialData.id) {
                // Update existing team
                response = await api.put(`/teams/${initialData.id}`, formData);
            } else {
                // Create new team
                response = await api.post('/teams', formData);
            }

            if (onSuccess) {
                onSuccess(response.data.team);
            }

            // Only reset if creating new, or close modal handles it
            if (!initialData) {
                resetForm();
            }
            onClose();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to save team');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            team_name: '',
            description: '',
            parent_id: '',
            leader_id: '',
            color: '#3b82f6',
        });
        setError('');
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-0 md:p-4">
            <div className="bg-card rounded-none md:rounded-2xl shadow-2xl w-full max-w-md h-full md:h-auto overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 md:p-6 border-b border-border shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="hidden md:block p-2 bg-primary/10 rounded-lg">
                            <Users size={24} className="text-primary" />
                        </div>
                        <div>
                            <h2 className="text-lg md:text-xl font-bold text-foreground">{initialData ? 'Update Team' : 'Create Team'}</h2>
                            <p className="text-xs md:text-sm text-muted-foreground">{initialData ? 'Edit team details' : 'Add a new team to your organization'}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg transition-colors">
                        <X size={20} className="text-muted-foreground" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4 md:space-y-5 flex-1 overflow-y-auto">
                    {error && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                            {error}
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-semibold text-foreground mb-2">
                            Team Name <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <Users size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <input
                                type="text"
                                name="team_name"
                                value={formData.team_name}
                                onChange={handleChange}
                                className="w-full pl-11 pr-4 py-3 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all bg-background text-foreground placeholder:text-muted-foreground"
                                placeholder="e.g., Engineering, Marketing, Sales"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-foreground mb-2">Description</label>
                        <div className="relative">
                            <FileText size={20} className="absolute left-3 top-3 text-muted-foreground" />
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                rows={3}
                                className="w-full pl-11 pr-4 py-3 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none bg-background text-foreground placeholder:text-muted-foreground"
                                placeholder="Brief description of this team..."
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-foreground mb-2">Parent Team</label>
                            <div className="relative">
                                <Users size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                <select
                                    name="parent_id"
                                    value={formData.parent_id}
                                    onChange={handleChange}
                                    className="w-full pl-11 pr-4 py-3 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none appearance-none bg-background text-foreground"
                                >
                                    <option value="">None (Top Level)</option>
                                    {existingTeams.filter(t => t.id !== (initialData?.id)).map(team => (
                                        <option key={team.id} value={team.id}>{team.team_name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-foreground mb-2">Team Lead</label>
                            <div className="relative">
                                <Users size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                <select
                                    name="leader_id"
                                    value={formData.leader_id}
                                    onChange={handleChange}
                                    className="w-full pl-11 pr-4 py-3 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none appearance-none bg-background text-foreground"
                                >
                                    <option value="">No Leader Assigned</option>
                                    {employees.map(emp => (
                                        <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-foreground mb-2">Team Color</label>
                        <div className="flex items-center gap-3">
                            {['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#6366f1'].map((color) => (
                                <button
                                    key={color}
                                    type="button"
                                    onClick={() => setFormData({ ...formData, color })}
                                    className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${formData.color === color ? 'border-foreground scale-110 ring-2 ring-offset-2 ring-primary' : 'border-transparent'}`}
                                    style={{ backgroundColor: color }}
                                />
                            ))}
                        </div>
                    </div>

                </form>

                {/* Footer */}
                <div className="flex items-center gap-3 p-4 md:p-6 border-t border-border bg-muted/30 shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-1 md:flex-none px-6 py-3 text-foreground hover:bg-muted rounded-xl font-semibold transition-colors border border-border md:border-transparent bg-card md:bg-transparent"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="flex-1 md:flex-none px-6 py-3 bg-primary hover:bg-primary/90 text-white rounded-xl font-semibold shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        {loading ? (
                            <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                {initialData ? 'Updating...' : 'Creating...'}
                            </>
                        ) : (
                            <>
                                <Save size={20} />
                                {initialData ? 'Update Team' : 'Create Team'}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreateTeamModal;
